import React, { useCallback, useContext, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, ButtonGroup, Heading } from '@superops/ui-design-system';

import 'react-tippy/dist/tippy.css';

import {
  AppContext,
  Tag,
  getTranslatedText,
  BackgroundIcon,
  AccessControl,
  getCurrentUserHook,
  EventEmitter,
  Loader,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  GET_STATUS_CONFIGURATION,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION,
  GET_TICKET_LISTVIEW_META,
  GET_ENTITY_PROPERTIES
} from '@nile/graphql';
import {
  DeleteConfirmation,
  MergeTicketSheet
} from '@nile/mangifera/src/Components/Sheets';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import AllTicketsContext from '@nile/client_management/src/Containers/ClientDetails/AllTicketsTable/AllTicketsContext';
import envConfig from '@nile/home/src/Config/env';
import ListViewColumns from '@nile/mangifera/src/Containers/SheetContainers/ListViewColumns';
import { SVGIcon } from '@superops/super-icons';

const StyledToolbar = styled.div`
  height: 90px;
  background: #ffffff;
  border-bottom: ${(props) => (props.border ? '1px solid #e7e7e7' : 'none')};
  padding: 20px 10px;
  .f11 {
    flex: 1 1;
  }
  .ticketing-modify {
    display: flex;
    align-items: center;
  }
  .navMenu .Superops-Button {
    padding: 0 10px !important;
  }
  .navMenu {
    margin-right: 20px !important;
  }
`;

const ToolbarWrapper = styled.div`
  .refresh .Superops-Button {
    padding: 0 10px !important;
  }
  .reset .Superops-Button--plain {
    margin: 0 !important;
    padding: 0 8px !important;
  }
  .tag {
    font-size: 12px !important;
    padding: 2px 6px !important;
    margin-left: 12px !important;
  }

  .globe {
    margin-left: 16px;
  }
  .view-type .Superops-ButtonGroup {
    align-items: flex-start;
  }
  .view-type .Superops-ButtonGroup__Item {
    margin: 0px;
    margin-left: 0px !important;
    .Superops-Button {
      padding: 0px !important;
    }
  }
`;

function TicketActionToolbar(props) {
  const { intl, context, appContext } = props;
  const {
    records,
    mergeTickets,
    softDeleteTickets,
    markSpamTickets,
    updateTickets
  } = context;
  const [selected, setSelected] = useState(null);
  const { data: statusConfig, loading, error } = useQuery(
    GET_STATUS_CONFIGURATION
  );

  const selectTicket = useCallback((value) => {
    setSelected(value);
  }, []);

  const assignToMe = () => {
    const { user } = appContext;
    let selectedTickets = context.records;
    selectedTickets = selectedTickets.map((el) => {
      return {
        ticketId: el.ticketId,
        technician: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          userId: user.userId
        }
      };
    });

    updateTickets({
      variables: { ticketInput: selectedTickets },
      input: selectedTickets
    });
  };

  const resolveTickets = () => {
    let selectedTickets = context.records;
    const status =
      statusConfig.getStatusConfiguration?.find(
        (el) => el.configuration === 'RESOLVE'
      )?.status?.name || null;
    selectedTickets = selectedTickets.map((el) => {
      return {
        ticketId: el.ticketId,
        status: status
      };
    });
    updateTickets({
      variables: { ticketInput: selectedTickets },
      input: selectedTickets
    });
  };

  const merge = (callback, variables) => {
    const parentId = selected?.[0] || records[0].ticketId;
    const childIds = records.reduce(function(filtered, item) {
      //Instant messaging source shouldnt be merged so filter it out for mergeTickets mutation
      if (
        item.ticketId !== parentId &&
        item.source !== TICKET_SOURCE.Communication
      ) {
        filtered.push(item.ticketId);
      }
      return filtered;
    }, []);
    mergeTickets({
      variables: {
        input: {
          parentId: parentId,
          childIds: childIds,
          ...variables
        }
      }
    }).then((res) => {
      if (callback) callback();
    });
  };

  const getTicketsIds = () => {
    return context.records.map((rec) => rec.ticketId);
  };

  const deleteTicket = () => {
    const ids = getTicketsIds();
    softDeleteTickets({
      variables: {
        ids: ids
      }
    });
  };

  const markTicketAsSpam = () => {
    const ids = getTicketsIds();
    markSpamTickets({
      variables: {
        ids: ids,
        markSpamRequester: true
      }
    });
  };

  return (
    <div className="mlauto">
      <ButtonGroup>
        <span className="f-16 fhp fx actr">
          <Button outline selected>
            {`${records.length} ${getTranslatedText(
              intl,
              'ticket.details.selected'
            )}`}
          </Button>
        </span>

        <AccessControl
          roleToCheck="tickets.edit"
          render={
            <Button
              onClick={assignToMe}
              icon={
                <div className="fx center">
                  <SVGIcon name="AssignToMeIcon" />
                </div>
              }
            >
              <FormattedMessage id="ticket.details.assign" />
            </Button>
          }
        />

        <AccessControl
          roleToCheck="tickets.edit"
          render={
            <Button
              onClick={resolveTickets}
              icon={
                <div className="fx center">
                  <SVGIcon name="Resolution" />
                </div>
              }
            >
              <FormattedMessage id="ticket.details.resolve" />
            </Button>
          }
        />

        <AccessControl
          roleToCheck="mergeTicket.allow"
          render={
            <MergeTicketSheet
              records={records}
              selectTicket={selectTicket}
              selected={selected || [records[0].ticketId]}
              merge={merge}
            />
          }
        />

        <AccessControl
          roleToCheck="spamTicket.allow"
          render={
            <Button
              onClick={markTicketAsSpam}
              icon={
                <div className="fx center">
                  <SVGIcon name="Spam" />
                </div>
              }
            >
              <FormattedMessage id="ticket.details.spam" />
            </Button>
          }
        />

        <AccessControl
          roleToCheck="tickets.delete"
          render={<DeleteConfirmation confirm={deleteTicket} />}
        />
      </ButtonGroup>
    </div>
  );
}

function TicketInfoToolbar(props) {
  const {
    context,
    activeView,
    params,
    allowCreate,
    columnPersonalisation,
    tableName,
    defaultColumns,
    intl
  } = props;
  const tabContext = useContext(TicketContext);

  const resetFilters = () => {
    context.setViewConditions(null);
  };

  return (
    <ToolbarWrapper
      className={`flex ${context.records.length === 0 ? 'full-width' : ''}`}
    >
      <div className="flex f11">
        <Heading element="h1">
          <div className="flex center fhp">{activeView.name}</div>
        </Heading>
      </div>

      <div className="flex center jend mlauto">
        {columnPersonalisation && (
          <TicketListColumns
            intl={intl}
            tableName={tableName}
            defaultColumns={defaultColumns}
          />
        )}
        {context.viewConditions && (
          <div className="ml8 mr8 reset">
            <Button id="ticketResetFilterToolbar" plain onClick={resetFilters}>
              <FormattedMessage id="ticket.details.resetFilters" />
            </Button>
          </div>
        )}
        <ButtonGroup>
          <AccessControl
            roleToCheck="tickets.add"
            render={
              <div>
                {allowCreate && (
                  <Button
                    primary
                    id="createTicket"
                    onClick={() => {
                      tabContext.openTab({
                        module: 'NEW',
                        id: 'new-tab',
                        state: {
                          ticket: {
                            subject: '',
                            client: { accountId: params.id },
                            requester: null
                          }
                        }
                      });
                    }}
                    icon={
                      <div className="fx center">
                        <SVGIcon name="Add" color="white" />
                      </div>
                    }
                  >
                    <FormattedMessage id="common.create" />
                  </Button>
                )}
              </div>
            }
          />
        </ButtonGroup>
      </div>
    </ToolbarWrapper>
  );
}

function TicketOnlyResetFilter(props) {
  const { context } = props;

  const resetFilters = () => {
    context.setViewConditions(null);
  };

  return (
    <ToolbarWrapper
      className={`flex ${context.records.length === 0 ? 'full-width' : ''}`}
    >
      <div className="flex center jend mlauto">
        {context.viewConditions && (
          <div className="ml8 mr8 reset">
            <Button id="ticketResetFilterToolbar" plain onClick={resetFilters}>
              <FormattedMessage id="ticket.details.resetFilters" />
            </Button>
          </div>
        )}
      </div>
    </ToolbarWrapper>
  );
}

const TicketListColumns = (props) => {
  const { intl, tableName, defaultColumns } = props;
  const user = getCurrentUserHook();
  let columns = [];

  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntities
  } = useQuery(GET_ENTITY_PROPERTIES);
  const {
    loading: ticketListViewMetaLoading,
    error: ticketListViewMetaError,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const {
    loading: columnLoading,
    error: columnError,
    data: columnsFromPersonalization,
    refetch: refetchColumns
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: user?.userId,
      category: 'AllTicketTableColumnPersonalization',
      name: tableName
    },
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(response) {
      // console.log(response, 'res TicketListColumns');
    }
  });

  const [allTicketTableColumnPersonalization] = useMutation(
    SAVE_PERSONALIZATION,
    {
      client: AccountClient,
      onCompleted({ savePersonalization }) {
        if (savePersonalization) {
          refetchColumns();
          EventEmitter.dispatch('refreshAllTableList');
        }
      },
      onError() {
        // toast('error', 'Something went wrong');
      }
    }
  );

  columns = columnsFromPersonalization?.getPersonalizationByCategoryAndName
    ?.value
    ? JSON.parse(
        columnsFromPersonalization?.getPersonalizationByCategoryAndName?.value
      ).columns
    : defaultColumns;

  columns = columns.map((el) => {
    return {
      ...el,
      // label: getTranslatedText(intl, el.label)
      label: el.label
    };
  });

  const setDefaultColumns = (data) => {
    const personalizedColumn = {
      user: {
        firstName: user?.firstName,
        userId: user?.userId,
        email: user?.email,
        lastName: user?.lastName
      },
      category: 'AllTicketTableColumnPersonalization',
      name: tableName,
      value: {
        columns: data
      }
    };

    allTicketTableColumnPersonalization({
      variables: {
        ...personalizedColumn,
        value: JSON.stringify(personalizedColumn.value)
      },
      input: {
        ...personalizedColumn,
        value: JSON.stringify(personalizedColumn.value)
      }
    });
  };

  const updateParent = (data) => {
    setDefaultColumns(data);
  };

  const sections = [];
  const fieldSection = {
    header: getTranslatedText(intl, 'ticket.columns.fields'),
    items: []
  };

  if (!entitiesLoading) {
    const Ticket = allEntities?.getEntitiesMeta
      .find((item) => {
        return item.name === 'Ticket';
      })
      .attributes.filter((el) => {
        return (
          ticketListViewMeta?.getTicketListViewMetaInfo?.selectAttributes.indexOf(
            el.name
          ) > -1
        );
      });
    const keys = Object.keys(Ticket);
    for (let k = 0; k < keys.length; k++) {
      fieldSection.items.push({
        ...Ticket[keys[k]],
        id: Ticket[keys[k]].name,
        label: getTranslatedText(
          intl,
          Ticket[keys[k]].label || Ticket[keys[k]].name
        )
      });
    }
  }

  sections.push(fieldSection);

  const loading = columnLoading || entitiesLoading;

  return (
    <>
      {columnLoading ? (
        <Loader />
      ) : (
        <div className="mr8">
          <ListViewColumns
            text={getTranslatedText(intl, 'ticket.icon.column')}
            // activator='Columns'
            columns={columns.length}
            sections={sections}
            meta={{
              heading: getTranslatedText(intl, 'ticket.columns.header'),
              selectedTitle: getTranslatedText(intl, 'ticket.columns.select')
            }}
            selectedItems={columns}
            updateParent={updateParent}
          />
        </div>
      )}
    </>
  );
};
function AllTicketsToolbar(props) {
  const {
    intl,
    match,
    allowCreate,
    columnPersonalisation,
    tableName,
    defaultColumns,
    onlyResetFilter
  } = props;

  const context = useContext(AllTicketsContext);
  const appContext = useContext(AppContext);
  const activeView = { name: 'All Tickets', id: 'all' };

  return !onlyResetFilter ? (
    <StyledToolbar className="fx actr">
      {context.records.length === 0 && (
        <>
          <TicketInfoToolbar
            intl={intl}
            activeView={activeView}
            context={context}
            appContext={appContext}
            params={match.params}
            allowCreate={allowCreate}
            columnPersonalisation={columnPersonalisation}
            tableName={tableName}
            defaultColumns={defaultColumns}
          />
        </>
      )}
      {context.records.length > 0 && (
        <TicketActionToolbar
          intl={intl}
          activeView={activeView}
          context={context}
          appContext={appContext}
        />
      )}
    </StyledToolbar>
  ) : (
    <TicketOnlyResetFilter context={context} />
  );
}

export default injectIntl(withRouter(AllTicketsToolbar));
